import React from 'react';
import { ArrowRight, Crown, X } from 'lucide-react';
import { useLanguage } from '../i18nContext';

interface CofounderWelcomeModalProps {
  onClose: () => void;
  onViewPricing: () => void;
}

export default function CofounderWelcomeModal({
  onClose,
  onViewPricing,
}: CofounderWelcomeModalProps) {
  const { t, language } = useLanguage();

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 isolate" role="dialog" aria-modal="true" aria-label="Co-founder Welcome" onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}>
      <div
        className="absolute inset-0 bg-ink-dark/20 backdrop-blur-md transform-gpu"
        style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' }}
        onClick={onClose}
      >
        <div className="absolute inset-0 bg-noise opacity-[0.03] pointer-events-none"></div>
      </div>

      <div className="relative z-10 w-full max-w-[480px] overflow-hidden rounded-modal bg-surface-light shadow-modal-heavy animate-in fade-in zoom-in-95 duration-500">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 z-20 rounded-full p-2 text-ink-muted transition-colors hover:bg-surface-hover hover:text-amber-700"
          aria-label={t.paymentModal.close}
        >
          <X size={18} />
        </button>

        {/* Amber glow effects */}
        <div className="absolute -left-16 -top-16 h-48 w-48 rounded-full bg-amber-200/30 blur-[40px] pointer-events-none"></div>
        <div className="absolute top-1/4 -right-12 h-40 w-40 rounded-full bg-yellow-200/15 blur-[40px] pointer-events-none"></div>
        <div className="absolute inset-0 bg-noise opacity-[0.02] pointer-events-none"></div>

        <div className="relative px-8 pt-12 pb-8 flex flex-col items-center text-center">

          {/* Central Logo Container */}
          <div className="relative mb-6">
            <div className="relative flex h-16 w-16 items-center justify-center rounded-[20px] bg-gradient-to-br from-amber-50 via-white to-yellow-50 border border-amber-200/80 shadow-[0_8px_20px_-6px_rgba(180,120,20,0.15)] ring-1 ring-white/60">
              <Crown className="text-amber-600" size={28} strokeWidth={2.5} style={{ filter: 'drop-shadow(0 2px 4px rgba(180,120,20,0.25))' }} />
            </div>
            {/* Organic Glow behind the logo */}
            <div className="absolute inset-0 -z-10 rounded-full bg-amber-300/40 blur-xl scale-125"></div>
          </div>

          <div className="mb-2 flex items-center gap-2">
            <div className="h-px w-6 bg-amber-200/50"></div>
            <span className="inline-block text-[10px] font-black uppercase tracking-[0.24em] text-amber-700">
              {t.paymentModal.cofounderExclusive}
            </span>
            <div className="h-px w-6 bg-amber-200/50"></div>
          </div>

          <h2 className="mb-4 text-3xl font-serif font-bold tracking-tight text-brand-900">
            {language === 'zh' ? (
              <>{t.paymentModal.cofounderWelcome}<span className="bg-gradient-to-r from-amber-800 via-yellow-700 to-amber-700 bg-clip-text text-transparent px-0.5">Co-founder</span></>
            ) : (
              <>Welcome, <span className="bg-gradient-to-r from-amber-800 via-yellow-700 to-amber-700 bg-clip-text text-transparent px-0.5">Co-founder</span></>
            )}
          </h2>

          <p className="mb-8 text-[15px] font-normal leading-relaxed text-ink-secondary max-w-[360px]">
            {t.paymentModal.cofounderDesc}
          </p>

          <div className="w-full relative overflow-hidden rounded-[20px] bg-gradient-to-br from-amber-50/60 to-surface-light border border-amber-200/70 p-5 mb-8 group/card">
            <div className="absolute right-0 top-0 w-32 h-32 bg-yellow-200/20 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2 transition-transform duration-700 group-hover/card:scale-150"></div>

            <div className="flex items-center justify-between relative z-10">
              <div className="text-left flex flex-col gap-0.5">
                <span className="text-[10px] font-bold uppercase tracking-widest text-amber-800/60">
                  {t.paymentModal.cofounderLifetime}
                </span>
                <span className="text-sm font-medium text-ink-dark">
                  {t.paymentModal.cofounderDiscount}
                </span>
              </div>
              <div className="text-3xl font-bold tracking-tight bg-gradient-to-br from-amber-800 to-amber-600 bg-clip-text text-transparent drop-shadow-sm">
                -20%
              </div>
            </div>
          </div>

          <div className="flex w-full flex-col gap-3">
            <button
              onClick={onViewPricing}
              className="w-full group flex items-center justify-center gap-2 rounded-xl border border-amber-600/20 bg-gradient-to-r from-amber-600 to-yellow-600 px-6 py-3.5 text-[15px] font-bold tracking-wide text-white shadow-[0_6px_20px_-4px_rgba(180,83,9,0.3)] transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_25px_-4px_rgba(180,83,9,0.4)] active:scale-[0.98]"
            >
              <span className="leading-none mt-px">{t.paymentModal.cofounderViewBenefits}</span>
              <ArrowRight size={18} strokeWidth={2.5} className="transition-transform group-hover:translate-x-1" />
            </button>
            <button
              onClick={onClose}
              className="w-full rounded-xl px-6 py-3 text-sm font-medium text-ink-muted transition-colors hover:bg-surface-hover hover:text-amber-800"
            >
              {t.paymentModal.cofounderMaybeLater}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
